import { Component, OnInit } from '@angular/core';
import {
  Category,
  CategoryScore,
  RiskLevel,
  PersonalInfo,
  Answers,
  UserAssessment
} from './hra.model';
import { serviceURLService } from '../service/serviceURL';

@Component({
  selector: 'app-hra',
  templateUrl: './hra.component.html',
  styleUrls: ['./hra.component.css']
})
export class HraComponent implements OnInit {
  screen: string = 'dashboard'; // Start with dashboard
  personal: PersonalInfo = { name: '', age: '', gender: '', relation: '' };
  answers: Answers = {};
  Object = Object;
  userAssessments: UserAssessment[] = [];
  isLoading: boolean = false;
  saveMessage: string = '';
  UserId: string = '';
  selectedUserForEdit: any = null;
  isInputDisabled: boolean = true;

  // Properties for previous report viewing
  isViewingPreviousReport: boolean = false;
  previousAnswers: Answers = {};
  previousCategoryScores: { [key: string]: CategoryScore } = {};
  previousTotalScore: number = 0;
  previousRiskLevel: RiskLevel = this.getRiskLevel(0);
  
  // New property for toggling previous score card
  showPreviousScoreCard: boolean = false;
  disableSaveButton: boolean = false;

  categories: Category[] = [
    {
      key: "lifestyle",
      title: "Lifestyle & Habits",
      max: 20,
      threshold: 20,
      questions: [
        {
          id: "smoking",
          label: "Smoking/Tobacco",
          options: [
            { label: "Non-smoker", score: 5 },
            { label: "Former smoker", score: 3 },
            { label: "Current smoker", score: 0 },
          ],
        },
        {
          id: "alcohol",
          label: "Alcohol Consumption",
          options: [
            { label: "Rare/None", score: 5 },
            { label: "Moderate", score: 3 },
            { label: "Heavy/Frequent", score: 0 },
          ],
        },
        {
          id: "activity",
          label: "Physical Activity",
          options: [
            { label: "Regular (3-5 times/week)", score: 5 },
            { label: "Occasional (1-2 times/week)", score: 3 },
            { label: "Sedentary", score: 0 },
          ],
        },
        {
          id: "diet",
          label: "Diet Quality",
          options: [
            { label: "Balanced", score: 5 },
            { label: "Average", score: 3 },
            { label: "Poor/Fast food often", score: 0 },
          ],
        },
      ],
    },
    {
      key: "medical",
      title: "Medical History",
      max: 20,
      threshold: 20,
      questions: [
        {
          id: "conditions",
          label: "Existing Conditions",
          options: [
            { label: "None", score: 10 },
            { label: "Mild (e.g. controlled thyroid)", score: 5 },
            { label: "Chronic (diabetes/HTN/etc.)", score: 0 },
          ],
        },
        {
          id: "meds",
          label: "Medications",
          options: [
            { label: "None", score: 5 },
            { label: "one chronic medication", score: 3 },
            { label: "two or more chronic medications", score: 0 },
          ],
        },
        {
          id: "hospital",
          label: "Hospitalizations (past 5 yrs)",
          options: [
            { label: "None", score: 5 },
            { label: "Minor", score: 3 },
            { label: "Major", score: 0 },
          ],
        },
      ],
    },
    {
      key: "physical",
      title: "Physical Measurements",
      max: 20,
      threshold: 20,
      questions: [
        {
          id: "bwp",
          label: "Body weight Perception",
          options: [
            { label: "Within healthy range", score: 5 },
            { label: "Slightly above or below healthy range", score: 3 },
            { label: "Significantly overweight or underweight", score: 0 }
          ],
        },
        {
          id: "abdominal",
          label: "Abdominal Fat",
          options: [
            { label: "No fat, feels healthy", score: 5 },
            { label: "Mild belly fat", score: 3 },
            { label: "Significant abdominal fat", score: 0 },
          ],
        },
        {
          id: "stamina",
          label: "Physical Endurance/Stamina",
          options: [
            { label: "Can perform daily tasks without fatigue", score: 5 },
            { label: "Mild fatigue during exertion", score: 3 },
            { label: "Easily fatigued during routine activities", score: 0 },
          ],
        },
        {
          id: "mobility",
          label: "Musculoskeletal Comfort & Mobility",
          options: [
            { label: "Rare or none", score: 5 },
            { label: "Occasional discomfort (back, neck, knees)", score: 3 },
            { label: "Frequent or persistent pain", score: 0 },
          ],
        }
      ],
    },
    {
      key: "family",
      title: "Family History",
      max: 20,
      threshold: 20,
      questions: [
        {
          id: "family_heart_disease",
          label: "Family History of Heart Disease",
          options: [
            { label: "No known history", score: 5 },
            { label: "One parent/sibling affected", score: 3 },
            { label: "Multiple close relatives affected", score: 0 }
          ],
        },
        {
          id: "family_diabetes",
          label: "Family History of Diabetes",
          options: [
            { label: "No known history", score: 5 },
            { label: "One parent/sibling affected", score: 3 },
            { label: "Multiple close relatives affected", score: 0 }
          ],
        },
        {
          id: "family_hypertension",
          label: "Family History of Hypertension (High BP)",
          options: [
            { label: "No known history", score: 5 },
            { label: "One parent/sibling affected", score: 3 },
            { label: "Multiple close relatives affected", score: 0 }
          ],
        },
        {
          id: "family_thyroid",
          label: "Family History of Thyroid Disorders",
          options: [
            { label: "No known history", score: 5 },
            { label: "One parent/sibling affected", score: 3 },
            { label: "Multiple close relatives affected", score: 0 }
          ],
        },
      ],
    },
    {
      key: "preventive",
      title: "Preventive Health",
      max: 5,
      threshold: 5,
      questions: [
        {
          id: "preventive",
          label: "Preventive Checkups",
          options: [
            { label: "Up to date", score: 5 },
            { label: "Partial", score: 3 },
            { label: "None", score: 0 },
          ]
        },
      ],
    },
    {
      key: "mental",
      title: "Mental Health",
      max: 15,
      threshold: 15,
      questions: [
        {
          id: "stress",
          label: "Stress Level",
          options: [
            { label: "Low", score: 5 },
            { label: "Moderate", score: 3 },
            { label: "High", score: 0 },
          ]
        },
        {
          id: "sleep",
          label: "Sleep Duration",
          options: [
            { label: "7-8 hours", score: 5 },
            { label: "5-6 hours", score: 3 },
            { label: "< 5 or < 9 hours", score: 0 },
          ]
        },
        {
          id: "screen_time",
          label: "Electronic Screen Usage Routine",
          options: [
            { label: "None", score: 5 },
            { label: "Occasional ", score: 3 },
            { label: "Consistent", score: 0 },
          ]
        },
      ],
    },
  ];

  private categoryScores: { [key: string]: CategoryScore } = {};
  private userPreviousScore: number = 0;

  constructor(private serviceURLService: serviceURLService) {
    this.UserId = localStorage.getItem('userID') || '';
  }

  ngOnInit(): void {
    this.initializeAnswers();
    this.calculateScores();
    this.loadUserAssessments();
  }

  private initializeAnswers(): void {
    const out: Answers = {};
    this.categories.forEach((cat) => {
      cat.questions.forEach((q) => (out[q.id] = null));
    });
    this.answers = out;
  }

  setAnswer(qid: string, score: number): void {
    this.answers[qid] = score;
    this.calculateScores();
  }

  calculateCategoryScore(category: Category): number {
    let score = 0;
    category.questions.forEach((q) => {
      const ans = this.answers[q.id];
      if (ans !== null && typeof ans === 'number') {
        score += ans;
      }
    });
    return score;
  }

  calculateScores(): void {
    const perCategory: { [key: string]: CategoryScore } = {};
    this.categories.forEach((cat) => {
      const score = this.calculateCategoryScore(cat);
      perCategory[cat.key] = { score, max: cat.max, threshold: cat.threshold };
    });
    this.categoryScores = perCategory;
  }

  // Calculate category scores from previous answers
  private calculatePreviousScores(): void {
    const perCategory: { [key: string]: CategoryScore } = {};

    this.categories.forEach((cat) => {
      let score = 0;
      cat.questions.forEach((q) => {
        const ans = this.previousAnswers[q.id];
        if (ans !== null && typeof ans === 'number') {
          score += ans;
        }
      });
      perCategory[cat.key] = { score, max: cat.max, threshold: cat.threshold };
    });

    this.previousCategoryScores = perCategory;
  }

  // Helper method to calculate total score from answers
  private calculateTotalScoreFromAnswers(answers: Answers): number {
    let total = 0;

    this.categories.forEach((cat) => {
      cat.questions.forEach((q) => {
        const ans = answers[q.id];
        if (ans !== null && typeof ans === 'number') {
          total += ans;
        }
      });
    });

    return total;
  }

  // Get total score (handles both current and previous report)
  get totalScore(): number {
    if (this.isViewingPreviousReport) {
      return this.previousTotalScore;
    }
    return Object.values(this.categoryScores).reduce((sum, cat) => sum + cat.score, 0);
  }

  getRiskLevel(total: number): RiskLevel {
    if (total > 95) return { label: "No Risk", tone: "success", className: "text-success" };
    if (total >= 81 && total <= 95) return { label: "Low Risk", tone: "success", className: "text-success" };
    if (total >= 61 && total <= 80) return { label: "Moderate Risk", tone: "warning", className: "text-warning" };
    return { label: "High Risk", tone: "danger", className: "text-danger" };
  }

  // Get risk level (handles both current and previous report)
  get riskLevel(): RiskLevel {
    if (this.isViewingPreviousReport) {
      return this.previousRiskLevel;
    }
    return this.getRiskLevel(this.totalScore);
  }

  setScreen(screenName: string): void {
    this.screen = screenName;
    this.saveMessage = '';
    this.showPreviousScoreCard = false; // Reset previous score card visibility
    this.disableSaveButton = false;

    // Reset viewing previous report flag when switching away from review
    if (screenName !== 'review') {
      this.isViewingPreviousReport = false;
      this.previousAnswers = {};
      this.previousCategoryScores = {};
      this.previousTotalScore = 0;
    }
    if (screenName === 'dashboard') {
      this.loadUserAssessments();
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  // Toggle previous score card visibility
  togglePreviousScoreCard(): void {
    this.showPreviousScoreCard = !this.showPreviousScoreCard;
  }

  // Check if current user has previous score
  hasPreviousScore(): boolean {
    const currentUser = this.userAssessments.find(user =>
      user.name === this.personal.name &&
      user.relation === this.personal.relation
    );
    return currentUser && currentUser.previousScore ? true : false;
  }

  getCategoryKeys(): string[] {
    if (this.isViewingPreviousReport) {
      return Object.keys(this.previousCategoryScores);
    }
    return Object.keys(this.categoryScores);
  }

  getCategoryScore(key: string): CategoryScore {
    if (this.isViewingPreviousReport && this.previousCategoryScores[key]) {
      return this.previousCategoryScores[key];
    }
    return this.categoryScores[key] || { score: 0, max: 0, threshold: 0 };
  }

  getCategoryTitle(key: string): string {
    const category = this.categories.find(cat => cat.key === key);
    return category ? category.title : key;
  }

  getFilteredRecommendations(categoryKey: string): string[] {
    // Use previous answers if viewing previous report
    const answersToUse = this.isViewingPreviousReport ? this.previousAnswers : this.answers;

    const recs: { [key: string]: string[] } = {
      lifestyle: [
        "Quit tobacco and reduce exposure to smoke.",
        "Limit alcohol to within recommended limits.",
        "Aim for 150 minutes of moderate activity per week.",
        "Increase fruit & vegetable intake; reduce processed foods.",
      ],
      medical: [
        "Complete regular monitoring for chronic conditions.",
        "Adhere to prescribed medications.",
        "Schedule follow-up with primary physician for management plan.",
      ],
      physical: [
        "Aim for gradual weight change (0.5–1 kg/month) through a balanced diet, increase daily physical activity (walking, stairs, stretching), and reduce sugary drinks and ultra-processed foods.",
        "Reduce refined carbs and sugary snacks, add core-strengthening exercises (planks, brisk walking), and avoid prolonged sitting by standing or stretching every 30–45 minutes.",
        "Start with 20–30 minutes of low-intensity activity (walking or cycling), improve sleep and hydration, and gradually increase activity intensity over 4–6 weeks.",
        "Follow simple daily stretching routines (5–10 minutes), improve workstation ergonomics, and incorporate light strength training or yoga twice weekly."
      ],
      family: [
        "Monitor BP and cholesterol regularly, follow a heart-healthy low-salt diet, stay active with 150+ minutes of exercise weekly, and get cardiac screening if advised.",
        "Maintain a healthy weight, limit high-sugar and refined carbs, increase fiber intake, stay physically active, and get an annual HbA1c test.",
        "Monitor BP regularly, limit salt intake to under 5g/day, and manage stress through yoga, deep breathing, or meditation.",
        "Get regular TSH screening, watch for symptoms like weight or mood changes, and maintain balanced iodine intake."
      ],
      preventive: [
        "Keep preventive checkups & vaccinations up to date."
      ],
      mental: [
        "Practice stress reduction (meditation/yoga) 10 mins daily.",
        "Establish sleep hygiene and maintain 7-8 hours of sleep.",
        "Create a 30-minute screen-free routine before bedtime (reading, stretching) and avoid using phones/laptops while in bed.",
      ],
    };

    let filteredRecs = recs[categoryKey] || ["General health advice: consult a physician."];

    // Filter recommendations based on selected options
    if (categoryKey === 'lifestyle') {
      if (answersToUse['smoking'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Quit tobacco"));
      }

      if (answersToUse['alcohol'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Limit alcohol"));
      }

      if (answersToUse['activity'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Aim for 150 minutes"));
      }

      if (answersToUse['diet'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Increase fruit & vegetable"));
      }
    }

    if (categoryKey === 'medical') {
      if (answersToUse['conditions'] === 10) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Complete regular monitoring"));
      }
      if (answersToUse['meds'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Adhere to prescribed medications"));
      }
      if (answersToUse['hospital'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Schedule follow-up"));
      }
    }

    if (categoryKey === 'physical') {
      if (answersToUse['bwp'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Aim for gradual weight change"));
      }
      if (answersToUse['abdominal'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Reduce refined carbs"));
      }
      if (answersToUse['stamina'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Start with 20–30 minutes"));
      }
      if (answersToUse['mobility'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Follow simple daily stretching"));
      }
    }

    if (categoryKey === 'family') {
      if (answersToUse['family_heart_disease'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Monitor BP and cholesterol"));
      }
      if (answersToUse['family_diabetes'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Maintain a healthy weight"));
      }
      if (answersToUse['family_hypertension'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Monitor BP regularly"));
      }
      if (answersToUse['family_thyroid'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Get regular TSH screening"));
      }
    }

    if (categoryKey === 'preventive') {
      if (answersToUse['preventive'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Keep preventive checkups"));
      }
    }

    if (categoryKey === 'mental') {
      if (answersToUse['stress'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Practice stress reduction"));
      }
      if (answersToUse['sleep'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Establish sleep hygiene"));
      }
      if (answersToUse['screen_time'] === 5) {
        filteredRecs = filteredRecs.filter(rec => !rec.includes("Create a 30-minute screen-free"));
      }
    }

    return filteredRecs;
  }

  shouldShowImprovement(categoryKey: string): boolean {
    // Use previous scores if viewing previous report
    const score = this.isViewingPreviousReport
      ? this.previousCategoryScores[categoryKey]
      : this.getCategoryScore(categoryKey);

    const filteredRecs = this.getFilteredRecommendations(categoryKey);

    // Only show if score is below threshold AND there are recommendations
    return score && score.score < score.threshold && filteredRecs.length > 0;
  }

  // Load User Assessments for Dashboard
  loadUserAssessments(): void {
    this.isLoading = true;

    // Simulating API call to get multiple user assessments
    this.serviceURLService.getUserHealthAssessment(this.UserId).subscribe((response: any) => {
      // Assuming response contains an array of user assessments
      this.userAssessments = response.response.userAssessments;
      this.isLoading = false;
    }, (error) => {
      console.log('Error fetching user assessments:', error);
      this.isLoading = false;
    });
  }

  saveAssessment(): void {
    this.isLoading = true;
    this.saveMessage = '';
    if (!this.isInputDisabled) {
      this.isLoading = false;
      this.saveMessage = 'Please review the score.';
      return;
    }
    const assessmentData = {
      userId: this.UserId,
      personalInfo: this.personal,
      answers: this.answers,
      scores: this.categoryScores,
      totalScore: this.totalScore,
      assessmentDate: new Date().toISOString()
    };

    // Simulating API call
    this.serviceURLService.saveUserHealthAssessment(assessmentData).subscribe((response: any) => {
      console.log('Assessment saved successfully:', response);
      this.isLoading = false;
      this.saveMessage = 'Health Risk Assessment score saved successfully.';
      // Reload assessments after saving
      this.disableSaveButton = true;
      this.loadUserAssessments();
    }, (error) => {
      console.log('Error saving assessment:', error);
      this.isLoading = false;
      this.saveMessage = 'Error saving assessment. Please try again.';
    });
  }


  getScreenTitle(): string {
    switch (this.screen) {
      case 'form': return 'Health Risk Assessment Form';
      case 'review': return this.isViewingPreviousReport ? 'Previous Health Report' : 'Health Score and Recommendations';
      case 'dashboard': return 'Dashboard';
      default: return 'Health Risk Assessment';
    }
  }

  // Get current user's previous score for review screen
  getCurrentUserPreviousScore(): number {
    if (this.isViewingPreviousReport) {
      return this.previousTotalScore;
    }

    const currentUser = this.userAssessments.find(user =>
      user.name === this.personal.name &&
      user.relation === this.personal.relation
    );
    return currentUser && currentUser.previousScore ? currentUser.previousScore.totalScore : 0;
  }

  // Get current user's previous risk level
  getCurrentUserPreviousRiskLevel(): RiskLevel {
    if (this.isViewingPreviousReport) {
      return this.previousRiskLevel;
    }

    const currentUser = this.userAssessments.find(user =>
      user.name === this.personal.name &&
      user.relation === this.personal.relation
    );
    return currentUser && currentUser.previousScore ? currentUser.previousScore.riskLevel : this.getRiskLevel(0);
  }

  getCurrentUserPreviousScoreDate(): string {
    const currentUser = this.userAssessments.find(user =>
      user.name === this.personal.name &&
      user.relation === this.personal.relation
    );
    return currentUser && currentUser.previousScore ? currentUser.previousScore.assessmentDate : '';
  }

  // View previous report
  viewPreviousReport(user: UserAssessment): void {
    // Set personal info from user
    this.personal.name = user.name;
    this.personal.age = user.age.toString();
    this.personal.gender = user.gender;
    this.personal.relation = user.relation;

    // Store the selected user for reference
    this.selectedUserForEdit = user;

    // Set flag to indicate we're viewing a previous report
    this.isViewingPreviousReport = true;

    // Load previous answers if available
    if (user.answers) {
      this.previousAnswers = user.answers;
    }

    // Calculate previous category scores based on answers
    this.calculatePreviousScores();

    // Set previous total score and risk level
    if (user.previousScore) {
      this.previousTotalScore = user.previousScore.totalScore;
      this.previousRiskLevel = user.previousScore.riskLevel;
    } else {
      this.previousTotalScore = this.calculateTotalScoreFromAnswers(this.previousAnswers);
      this.previousRiskLevel = this.getRiskLevel(this.previousTotalScore);
    }

    // Navigate to review screen
    this.setScreen('review');
  }

  // Start new assessment for a user from dashboard
  startNewAssessment(user: UserAssessment): void {
    // Reset viewing previous report flag
    this.isViewingPreviousReport = false;
    this.previousAnswers = {};
    this.previousCategoryScores = {};
    this.previousTotalScore = 0;
    this.showPreviousScoreCard = false;

    this.isInputDisabled = true;
    this.selectedUserForEdit = user;
    this.personal.name = user.name;
    this.personal.age = user.age.toString();
    this.personal.gender = user.gender;
    this.personal.relation = user.relation;

    // Check if previousScore exists before accessing it
    this.userPreviousScore = user.previousScore ? user.previousScore.totalScore : 0;

    // Reset answers
    this.initializeAnswers();
    this.calculateScores();

    // Navigate to form
    this.setScreen('form');
  }

  // Clear form and start fresh
  startFreshAssessment(): void {
    // Reset viewing previous report flag
    this.isViewingPreviousReport = false;
    this.previousAnswers = {};
    this.previousCategoryScores = {};
    this.previousTotalScore = 0;
    this.showPreviousScoreCard = false;

    this.isInputDisabled = false;
    this.selectedUserForEdit = null;
    this.personal = { name: '', age: '', gender: '', relation: '' };
    this.initializeAnswers();
    this.calculateScores();
    this.setScreen('form');
  }

  // Get relation options
  getRelationOptions(): string[] {
    return ['SELF', 'WIFE', 'HUSBAND', 'SON', 'DAUGHTER', 'FATHER', 'MOTHER', 'BROTHER', 'SISTER', 'OTHER'];
  }

  // Helper method to get current date
  get today(): Date {
    return new Date();
  }

  // Add this simple method to check if form is complete
  isFormComplete(): boolean {
    // Check if all questions are answered
    for (const category of this.categories) {
      for (const question of category.questions) {
        if (this.answers[question.id] === null || this.answers[question.id] === undefined) {
          return false;
        }
      }
    }
    return true;
  }

}